import { useCallback, useMemo } from 'react';
import Table from '@antimatterhq/ui/components/table';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import { useTableColumnSorting } from '@antimatterhq/ui/hooks/table/useTableColumnSorting';
import { useVirtuosoTableRows } from '@antimatterhq/ui/hooks/table/useVirtuosoTableRows';
import { content_v1_PresetDataSourceList } from '@antimatterhq/asl-api-ts-v1';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { useNavigate } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useSetAtom } from 'jotai';
import { TableColumnDefinition } from '@antimatterhq/ui/types/TableColumnDefinition';
import {
  tableColumnDefinitionsWithFilteringDisabled,
  tableColumnDefinitionsWithTooltipDisabled,
} from '@antimatterhq/ui/utils/table/helpers';
import { DataSourceIcon } from '@/components/dataSources/dataSourceCardAndTable/dataSourceIcon/DataSourceIcon';
import { showRequestNewDatasourceDialogAtom } from '@/atoms/dataSources/dataSourcesAtoms';
import { Medallion } from '@/components/medallion/Medallion';

export const getAddDataSourcesTableColumnDefinitions =
  (): TableColumnDefinition[] => {
    const baseColumns: TableColumnDefinition[] = [
      {
        id: 'name',
        title: 'Source',
        width: 500,
      },
      {
        id: 'image',
        title: '',
        width: 56,
        enableResize: false,
        enableSorting: false,
        enableFiltering: false,
      },
      {
        id: 'medallion',
        title: '',
        width: 56,
        enableResize: false,
        enableSorting: false,
        enableFiltering: false,
      },
      {
        id: 'actions',
        title: '',
        width: 300,
        enableSorting: false,
        enableFiltering: false,
        enableResize: false,
        pinned: 'right',
      },
    ];

    const withTooltipDisabled =
      tableColumnDefinitionsWithTooltipDisabled(baseColumns);
    return tableColumnDefinitionsWithFilteringDisabled(withTooltipDisabled);
  };

interface AddDataSourceTableData {
  id: string;
  name: string;
  title: string;
  description: string;
  author: string;
  sourceType: string;
  iconURL: string;
  type: 'preset' | 'custom' | 'request';
  targetMedallion: 'bronze' | 'silver' | 'gold' | null;
}

interface AddDataSourcesTableProps {
  dataSourcePresets: content_v1_PresetDataSourceList;
  isLoading?: boolean;
}

const transformPresetData = (
  preset: content_v1_PresetDataSourceList['items'][0],
): AddDataSourceTableData => {
  return {
    id: preset.name || '',
    title: preset.title || '',
    name: preset.name || '',
    description: preset.description || '',
    author: preset.author || '',
    sourceType: preset.sourceType || '',
    iconURL: preset.iconURL || '',
    type: 'preset',
    targetMedallion: preset.targetMedallion as
      | 'bronze'
      | 'silver'
      | 'gold'
      | null,
  };
};

function AddDataSourcesTableContent({
  dataSourcePresets,
}: AddDataSourcesTableProps) {
  const navigate = useNavigate();
  const { enableCustomDatasources, enableRequestDatasource } =
    useFlags<FlagSet>();
  const setShowRequestDialog = useSetAtom(showRequestNewDatasourceDialogAtom);

  const items = useMemo(() => {
    const allItems: AddDataSourceTableData[] = [];

    if (enableCustomDatasources) {
      allItems.push({
        id: 'custom',
        name: 'Custom Data Source',
        title: 'Custom Data Source',
        description: 'Create a custom data source',
        author: '',
        sourceType: '',
        iconURL: '',
        type: 'custom',
        targetMedallion: null,
      });
    }

    allItems.push(...dataSourcePresets.items.map(transformPresetData));

    if (enableRequestDatasource) {
      allItems.push({
        id: 'request',
        name: 'Request New Data Source',
        title: 'Request New Data Source',
        description: 'Request a new data source preset',
        author: '',
        sourceType: '',
        iconURL: '',
        type: 'request',
        targetMedallion: null,
      });
    }

    return allItems;
  }, [dataSourcePresets, enableCustomDatasources, enableRequestDatasource]);

  // Sorts the items for specific columns
  const { sortedItems, setSortBy, sortKey, sortDirection } =
    useTableColumnSorting(items);

  // Creates & manages the columns
  const { columns, columnVisibility, columnWidths } = useTableColumns({
    sortKey,
    sortDirection,
    setSortBy,
  });

  const handleAddClick = useCallback(
    (item: AddDataSourceTableData) => {
      if (item.type === 'custom') {
        navigate('/configure/data-sources/add/source');
      } else if (item.type === 'request') {
        setShowRequestDialog(true);
      } else {
        // For presets, navigate with query params
        const preset = dataSourcePresets.items.find(
          (p) => p.name === item.name,
        );
        if (preset) {
          navigate(
            `/configure/data-sources/add/getting-started?source=${preset.source}&sourceType=${preset.sourceType}&usePreset=${preset.name}`,
          );
        }
      }
    },
    [navigate, dataSourcePresets, setShowRequestDialog],
  );

  const { fixedHeaderContent, itemContent } =
    useVirtuosoTableRows<AddDataSourceTableData>({
      columns,
      isLoading: false,
      customBodyCells: useMemo(
        () => ({
          name: ({ item }) => (
            <div className="flex items-center gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium">{item.title}</div>
                {item.description && (
                  <div className="truncate text-xs text-gray-500">
                    {item.description}
                  </div>
                )}
              </div>
            </div>
          ),
          image: ({ item }) => (
            <div className="flex w-full items-center gap-2">
              {item.type === 'custom' || item.type === 'request' ? (
                <Icon
                  icon={'antimatterhq:custom-datasource'}
                  className="text-blue-500"
                  width={32}
                  data-testid={`${item.id}-icon`}
                />
              ) : (
                <DataSourceIcon
                  iconURL={item.iconURL}
                  name={item.name}
                  className="h-8 w-8 flex-shrink-0"
                />
              )}
            </div>
          ),
          medallion: ({ item }) => {
            if (!item.targetMedallion) {
              return null;
            }
            return (
              <div className="flex w-full justify-center">
                <Medallion
                  medallion={
                    item.targetMedallion as 'bronze' | 'silver' | 'gold'
                  }
                />
              </div>
            );
          },
          actions: ({ item }) => {
            return (
              <Button
                onClick={() => handleAddClick(item)}
                size="default"
                variant="default"
                data-testid={`add-datasource-${item.id}`}
                className="ml-auto"
              >
                {item.id === 'request'
                  ? 'Request a new data source integration'
                  : item.id === 'custom'
                    ? 'Add custom data source'
                    : 'Add'}
              </Button>
            );
          },
        }),
        [handleAddClick],
      ),
      'data-testid': 'add-data-sources-table',
    });

  return (
    <Table.VirtualizedPageContainer className="h-[calc(100vh-224px)]">
      <Table.StyleProvider
        virtualized={true}
        pageSize={sortedItems.length}
        columnWidths={columnWidths}
        columnVisibility={columnVisibility}
        data-testid="add-data-sources-table-style-provider"
      >
        <Table.Virtual
          columns={columns}
          data={sortedItems}
          isLoading={false}
          itemContent={itemContent}
          fixedHeaderContent={fixedHeaderContent}
          emptyMessage="No data source presets available"
          itemHeight={64}
        />
      </Table.StyleProvider>
    </Table.VirtualizedPageContainer>
  );
}

export function AddDataSourcesTable(props: AddDataSourcesTableProps) {
  const columnDefinitions = useMemo(
    () => getAddDataSourcesTableColumnDefinitions(),
    [],
  );

  return (
    <Table.Provider
      columnDefinitions={columnDefinitions}
      localStorageKeyPrefix="add-data-sources-table"
    >
      <AddDataSourcesTableContent {...props} />
    </Table.Provider>
  );
}
